import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, EmptyState, Notice, Skeleton, scrollRegion, type Tone } from '@agency/ui';
import type { ApiClientError } from '../../lib/api.ts';
import { get } from '../../lib/api.ts';
import { QueryError } from '../../lib/query-state.tsx';

type Overview = {
  counts: {
    published: number; indexIntended: number; noindex: number; missingMetadata: number; brokenLinks: number; canonicalProblems: number;
    withBlockers: number; withWarnings: number; unpublishedDrafts: number; missingLocalizedVersions: number; redirects: number; redirectProblems: number; notFoundPaths: number;
  };
  issuesByCheck: { key: string; pages: number }[];
  issues: { variantId: string; name: string; locale: string; slug: string; failing: string[]; warnings: string[] }[];
  searchConsole: { status: 'not_connected' };
};

/** Plain-language names for the check keys the server reports. Unknown keys fall back to the key itself. */
export const CHECK_LABEL: Record<string, string> = {
  'title-present': 'Title exists', 'title-length': 'Title length', 'description-present': 'Meta description exists', 'description-length': 'Description length',
  'slug-valid': 'URL path is valid', 'slug-localized': 'URL path is localized', 'canonical-valid': 'Canonical URL', 'hreflang-valid': 'Language alternates',
  'missing-translations': 'Language versions', 'unique-title': 'Title is unique', 'unique-description': 'Description is unique', 'og-image': 'Social sharing image',
  'alt-text': 'Image alternative text', 'internal-links': 'Internal links', 'broken-links': 'Broken internal links', 'fallback-links': 'Links to untranslated pages',
  'draft-links': 'Links to pages that are not live', indexability: 'Indexing', 'structured-data': 'Structured data', orphan: 'Page is linked from elsewhere',
  placeholder: 'Placeholder copy', form: 'Lead form', 'locale-enabled': 'Language is enabled', 'unverified-claims': 'Claims awaiting verification', 'restricted-category': 'Restricted advertising category',
};
const label = (k: string) => CHECK_LABEL[k] ?? k;

function Stat({ name, value, hint, tone }: { name: string; value: number; hint?: string; tone?: Tone }) {
  return (
    <div className="seo-stat">
      <dt>{name}</dt>
      <dd><span className="seo-stat__n">{value}</span>{tone && value > 0 ? <Badge tone={tone}>Needs attention</Badge> : null}</dd>
      {hint ? <dd className="ag-muted seo-stat__hint">{hint}</dd> : null}
    </div>
  );
}

export function OverviewTab({ goTo }: { goTo: (tab: 'redirects' | 'not-found') => void }) {
  const q = useQuery<Overview, ApiClientError>({ queryKey: ['seo', 'overview'], queryFn: () => get('/admin/seo/overview') });
  if (q.isLoading) return <Skeleton width="100%" height={260} />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  const { counts: c, issues, issuesByCheck } = q.data!;
  const empty = c.published + c.unpublishedDrafts === 0;
  return (
    <div className="stack">
      <Notice tone="info" title="Search Console is not connected" live={false}>Clicks, impressions and rankings are not shown because there is no verified data source yet. The figures below come from this site’s own pages and redirects.</Notice>
      {empty ? <EmptyState title="No pages to check yet" icon="layout" action={<Link to="/landings" className="ag-btn" style={{ textDecoration: 'none' }}>Go to Landings</Link>}>Create and publish a landing page to see its search checks here.</EmptyState> : (
        <>
          <section className="panel" aria-labelledby="seo-pages-h">
            <h2 id="seo-pages-h">Pages</h2>
            <dl className="seo-stats">
              <Stat name="Published" value={c.published} hint="Live on the public site" />
              <Stat name="Open to indexing" value={c.indexIntended} hint="Published, robots set to index" />
              <Stat name="Set to noindex" value={c.noindex} hint="Published but hidden from search" />
              <Stat name="Unpublished drafts" value={c.unpublishedDrafts} hint="Checked on their draft content" />
            </dl>
          </section>
          <section className="panel" aria-labelledby="seo-problems-h">
            <h2 id="seo-problems-h">Problems found</h2>
            <dl className="seo-stats">
              <Stat name="With blockers" value={c.withBlockers} tone="error" hint="Cannot be published until fixed" />
              <Stat name="With warnings" value={c.withWarnings} tone="warning" hint="Can be published, worth fixing" />
              <Stat name="Missing title or description" value={c.missingMetadata} tone="warning" />
              <Stat name="Broken internal links" value={c.brokenLinks} tone="error" hint="Pages with at least one" />
              <Stat name="Canonical problems" value={c.canonicalProblems} tone="error" />
              <Stat name="Missing language versions" value={c.missingLocalizedVersions} tone="warning" hint="Page and enabled language pairs without a version" />
            </dl>
          </section>
          <section className="panel" aria-labelledby="seo-redirects-h">
            <h2 id="seo-redirects-h">Redirects and missing pages</h2>
            <dl className="seo-stats">
              <Stat name="Redirects" value={c.redirects} />
              <Stat name="Redirect problems" value={c.redirectProblems} tone="error" hint="Redirect loops, chains or self-redirects" />
              <Stat name="Paths returning 404" value={c.notFoundPaths} tone="warning" hint="Not dismissed" />
            </dl>
            <div className="actions__row">
              <button type="button" className="ag-btn ag-btn--sm" onClick={() => goTo('redirects')}>Review redirects</button>
              <button type="button" className="ag-btn ag-btn--sm" onClick={() => goTo('not-found')}>Review 404s</button>
            </div>
          </section>
          <section className="panel" aria-labelledby="seo-bycheck-h">
            <h2 id="seo-bycheck-h">Most common issues</h2>
            {issuesByCheck.length === 0 ? <p className="ag-muted">No failing checks or warnings on any page.</p> : (
              <div className="ag-table-wrap" {...scrollRegion('Issues by check')}>
                <table className="ag-table">
                  <caption className="ag-visually-hidden">Issues by check, most affected first</caption>
                  <thead><tr><th scope="col">Check</th><th scope="col" className="ag-num">Pages affected</th></tr></thead>
                  <tbody>{issuesByCheck.map((i) => <tr key={i.key}><td>{label(i.key)}</td><td className="ag-num">{i.pages}</td></tr>)}</tbody>
                </table>
              </div>
            )}
          </section>
          <section className="panel" aria-labelledby="seo-issues-h">
            <h2 id="seo-issues-h">Published pages with issues</h2>
            {issues.length === 0 ? <p className="ag-muted">No published page has failing checks or warnings.</p> : (
              <>
                <div className="ag-table-wrap" {...scrollRegion('Published pages with issues')}>
                  <table className="ag-table">
                    <caption className="ag-visually-hidden">Published pages with failing checks or warnings</caption>
                    <thead><tr><th scope="col">Page</th><th scope="col">Language</th><th scope="col">Failing</th><th scope="col">Warnings</th><th scope="col"><span className="ag-visually-hidden">Open</span></th></tr></thead>
                    <tbody>
                      {issues.map((i) => (
                        <tr key={i.variantId}>
                          <td><span className="ag-cell-2"><span>{i.name}</span><span className="ag-mono">/{i.slug}</span></span></td>
                          <td>{i.locale}</td>
                          <td>{i.failing.length ? <span className="seo-badges">{i.failing.map((k) => <Badge key={k} tone="error">{label(k)}</Badge>)}</span> : <span className="ag-muted">None</span>}</td>
                          <td>{i.warnings.length ? <span className="seo-badges">{i.warnings.map((k) => <Badge key={k} tone="warning">{label(k)}</Badge>)}</span> : <span className="ag-muted">None</span>}</td>
                          <td style={{ textAlign: 'right' }}><Link to={`/landings/${i.variantId}`} className="ag-btn ag-btn--sm" style={{ textDecoration: 'none' }}>Open<span className="ag-visually-hidden"> {i.name} ({i.locale}) in the editor</span></Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {issues.length >= 100 ? <p className="ag-muted">Showing the first 100 pages. Fix some and refresh to see the rest.</p> : null}
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
